/**
 * Улучшенный Text-to-Speech модуль с поддержкой:
 * 1. Web Speech API (встроенный, бесплатный)
 * 2. Edge TTS на сервере (/api/tts/synthesize)
 */

import { API_ORIGIN } from '../services/api';

/** Из ShortName Edge (en-US-GuyNeural) — BCP-47 для Web Speech API */
function localeFromEdgeVoiceId(voiceId) {
    if (!voiceId || typeof voiceId !== 'string') return 'en-US';
    const m = voiceId.match(/^(en-[A-Z]{2})-/i);
    if (m) return m[1];
    if (voiceId.startsWith('en-')) return 'en-US';
    return 'en-US';
}

/** Подобрать системный голос под выбранный в уроке Edge-voice (имена не совпадают 1:1) */
function pickWebSpeechVoiceForEdgeId(edgeVoiceId, allVoices, fallbackVoice) {
    if (!allVoices || !allVoices.length) return fallbackVoice;
    const id = String(edgeVoiceId || '');
    const lang = localeFromEdgeVoiceId(id);
    const primary = lang.split('-')[0].toLowerCase();
    const pool = allVoices.filter(
        (v) => v.lang === lang || v.lang.startsWith(`${primary}-`) || v.lang === primary
    );
    const usePool = pool.length ? pool : allVoices;

    const male =
        /Guy|Christopher|Ryan|Thomas|William|Liam|Brian|Andrew|David|Fred|Daniel|Mark|George/i.test(id);
    const female =
        /Jenny|Aria|Michelle|Sonia|Libby|Natasha|Clara|Ana|Ava|Maisie|Emma|Michelle/i.test(id);

    const nameMale = (n) =>
        /Male|David|Guy|Fred|Daniel|Mark|George|Aaron|Arthur|Oliver|Thomas|Ryan|William|Liam/i.test(n);
    const nameFemale = (n) =>
        /Female|Zira|Samantha|Victoria|Karen|Susan|Moira|Fiona|Kate|Tessa|Martha|Jenny|Aria/i.test(n);

    if (male) {
        const v = usePool.find((x) => nameMale(x.name));
        if (v) return v;
    }
    if (female) {
        const v = usePool.find((x) => nameFemale(x.name));
        if (v) return v;
    }
    return usePool[0] || fallbackVoice;
}

class TextToSpeechService {
    constructor() {
        this.synth = window.speechSynthesis;
        this.voices = [];
        this.preferredVoice = null;
        this.isInitialized = false;
        this.useCloudTTS = false; // По умолчанию используем Web Speech API
        this.cloudTTSAvailable = false;
        this.currentAudio = null; // Текущее Edge/Cloud‑аудио
        this._cloudInitPromise = null;
        this._warnedEdgeTtsUnavailable = false;

        // Инициализация
        this.initVoices();
        this._cloudInitPromise = this.runCloudTtsProbe();
    }

    /**
     * Проверить доступность Edge TTS на сервере (один раз; speak() ждёт завершения)
     */
    async runCloudTtsProbe() {
        try {
            const response = await fetch(`${API_ORIGIN}/api/tts/status`);
            const data = await response.json();
            this.cloudTTSAvailable = !!data.available;

            if (this.cloudTTSAvailable) {
                this.useCloudTTS = true;
                console.log(`${data.provider || 'Cloud TTS'} доступен и включен по умолчанию`);
            } else {
                console.log('Cloud TTS не доступен, используем Web Speech API');
            }
        } catch (error) {
            console.log('Cloud TTS не доступен, используем Web Speech API');
            this.cloudTTSAvailable = false;
        }
    }

    async ensureCloudReady() {
        if (this._cloudInitPromise) {
            await this._cloudInitPromise;
        }
    }

    /**
     * Получить список голосов Edge TTS
     */
    async getEdgeVoices() {
        try {
            const response = await fetch(`${API_ORIGIN}/api/tts/voices`);
            const data = await response.json();
            return data.voices || [];
        } catch (error) {
            console.error('Ошибка получения голосов Edge TTS:', error);
            return [];
        }
    }

    /** Запросы вида *Neural — только через Edge TTS на сервере */
    _isEdgeNeuralVoiceId(voiceId) {
        return typeof voiceId === 'string' && /Neural/i.test(voiceId);
    }

    initVoices() {
        // Загрузка голосов может занять время
        const loadVoices = () => {
            this.voices = this.synth.getVoices();
            
            // Выбираем лучший английский голос
            this.preferredVoice = this.findBestEnglishVoice();
            this.isInitialized = true;
        };

        // Голоса могут загружаться асинхронно
        if (this.synth.onvoiceschanged !== undefined) {
            this.synth.onvoiceschanged = loadVoices;
        }
        
        // Попробуем загрузить сразу
        loadVoices();
    }

    findBestEnglishVoice() {
        // Приоритет голосов (от лучшего к худшему)
        const preferredVoices = [
            // Google voices (лучшее качество)
            'Google US English',
            'Google UK English Female',
            'Google UK English Male',
            
            // Microsoft voices (хорошее качество)
            'Microsoft Zira - English (United States)',
            'Microsoft David - English (United States)',
            
            // Apple voices (естественные)
            'Samantha',
            'Alex',
            'Victoria',
            
            // Общие английские
            'English United States',
            'English (United States)'
        ];

        // Ищем по приоритету
        for (const voiceName of preferredVoices) {
            const voice = this.voices.find(v => 
                v.name.includes(voiceName) || v.name === voiceName
            );
            if (voice) return voice;
        }

        // Если не нашли, берем любой английский
        const anyEnglish = this.voices.find(v => 
            v.lang.startsWith('en-') || v.lang === 'en'
        );
        
        return anyEnglish || this.voices[0];
    }

    /**
     * Проговорить текст
     * @param {string} text - Текст для произношения
     * @param {Object} options - Опции (rate, pitch, volume, useCloud)
     */
    async speak(text, options = {}) {
        await this.ensureCloudReady();

        const neural = this._isEdgeNeuralVoiceId(options.voice);
        // Для нейросетевых имён обязан работать серверный Edge TTS; иначе браузер всё равно не знает такой голос
        let useCloud =
            options.useCloud !== undefined ? options.useCloud : neural ? true : this.useCloudTTS;

        if (neural && !this.cloudTTSAvailable) {
            useCloud = false;
            if (!this._warnedEdgeTtsUnavailable) {
                this._warnedEdgeTtsUnavailable = true;
                console.warn(
                    'Edge TTS на сервере недоступен (установите edge-tts на сервере и проверьте REACT_APP_API_URL). Нейроголоса заменяются браузером. Проверка:',
                    `${API_ORIGIN}/api/tts/status`
                );
            }
        }

        if (useCloud && this.cloudTTSAvailable) {
            return this.speakWithCloudTTS(text, options);
        }
        return this.speakWithWebAPI(text, options);
    }

    /**
     * Использовать Web Speech API
     */
    speakWithWebAPI(text, options = {}) {
        return new Promise((resolve, reject) => {
            if (!this.isInitialized) {
                this.initVoices();
            }

            const liveVoices = this.synth.getVoices();
            if (liveVoices.length) {
                this.voices = liveVoices;
                if (!this.preferredVoice) {
                    this.preferredVoice = this.findBestEnglishVoice();
                }
            }

            // Остановить предыдущее воспроизведение
            this.stop();

            const utterance = new SpeechSynthesisUtterance(text);

            const lang =
                options.lang || localeFromEdgeVoiceId(options.voice) || 'en-US';
            const matched = pickWebSpeechVoiceForEdgeId(
                options.voice,
                this.voices.length ? this.voices : liveVoices,
                this.preferredVoice
            );

            utterance.voice = matched || this.preferredVoice;
            utterance.lang = lang;
            utterance.rate = options.rate || 1.0; // 0.1 - 10 (норма 1)
            utterance.pitch = options.pitch || 1.0; // 0 - 2 (норма 1)
            utterance.volume = options.volume || 1.0; // 0 - 1

            // События
            utterance.onend = () => resolve();
            utterance.onerror = (error) => reject(error);

            this.synth.speak(utterance);
        });
    }

    /**
     * Использовать Google Cloud TTS
     */
    async speakWithCloudTTS(text, options = {}) {
        try {
            // Конвертируем имена голосов для Edge TTS
            let voiceName = options.voice || 'en-US-JennyNeural';
            
            // Если передан Google voice, конвертируем в Edge voice
            if (voiceName.includes('Neural2') || voiceName.includes('Wavenet')) {
                voiceName = 'en-US-JennyNeural'; // Default Edge voice
            }
            
            const response = await fetch(`${API_ORIGIN}/api/tts/synthesize`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    text,
                    voice: voiceName,
                    speed: options.rate || 1.0
                })
            });

            if (!response.ok) {
                const error = await response.json();
                if (error.fallback) {
                    // Fallback на Web Speech API
                    console.log('Fallback на Web Speech API');
                    return this.speakWithWebAPI(text, options);
                }
                throw new Error(error.error || 'Ошибка генерации аудио');
            }

            const audioBlob = await response.blob();
            const audioUrl = URL.createObjectURL(audioBlob);
            
            return new Promise((resolve, reject) => {
                // Останавливаем предыдущее аудио, если было
                if (this.currentAudio) {
                    try {
                        this.currentAudio.pause();
                    } catch (e) {}
                    this.currentAudio = null;
                }

                const audio = new Audio(audioUrl);
                this.currentAudio = audio;

                // Колбэки
                if (typeof options.onProgress === 'function') {
                    audio.ontimeupdate = () => {
                        if (audio.duration && !isNaN(audio.duration)) {
                            const progress = audio.currentTime / audio.duration;
                            options.onProgress(Math.max(0, Math.min(1, progress)));
                        }
                    };
                }

                // onStart вызываем один раз при первом запуске
                if (typeof options.onStart === 'function') {
                    options.onStart();
                }

                audio.onended = () => {
                    URL.revokeObjectURL(audioUrl);
                    this.currentAudio = null;
                    if (typeof options.onEnd === 'function') {
                        options.onEnd();
                    }
                    resolve();
                };

                audio.onerror = (error) => {
                    URL.revokeObjectURL(audioUrl);
                    this.currentAudio = null;
                    reject(error);
                };

                audio.play();
            });

        } catch (error) {
            console.error('Ошибка Cloud TTS, используем Web Speech API:', error);
            return this.speakWithWebAPI(text, options);
        }
    }

    /**
     * Включить/выключить Google Cloud TTS
     */
    setUseCloudTTS(enabled) {
        this.useCloudTTS = enabled && this.cloudTTSAvailable;
        return this.useCloudTTS;
    }

    /**
     * Проверить доступность Cloud TTS
     */
    isCloudTTSAvailable() {
        return this.cloudTTSAvailable;
    }

    /**
     * Остановить воспроизведение
     */
    stop() {
        // Останавливаем Web Speech
        if (this.synth.speaking) {
            this.synth.cancel();
        }

        // Останавливаем Edge/Cloud‑аудио
        if (this.currentAudio) {
            try {
                this.currentAudio.pause();
                this.currentAudio.currentTime = 0;
            } catch (e) {}
            this.currentAudio = null;
        }
    }

    /**
     * Алиас для stop() для совместимости
     */
    cancel() {
        this.stop();
    }

    /**
     * Пауза
     */
    pause() {
        // Пауза Web Speech
        if (this.synth.speaking) {
            this.synth.pause();
        }

        // Пауза Edge/Cloud‑аудио
        if (this.currentAudio && !this.currentAudio.paused) {
            try {
                this.currentAudio.pause();
            } catch (e) {}
        }
    }

    /**
     * Продолжить
     */
    resume() {
        // Продолжить Web Speech
        if (this.synth.paused) {
            this.synth.resume();
        }

        // Продолжить Edge/Cloud‑аудио
        if (this.currentAudio && this.currentAudio.paused) {
            try {
                this.currentAudio.play();
            } catch (e) {}
        }
    }

    /**
     * Проверка поддержки
     */
    isSupported() {
        return 'speechSynthesis' in window;
    }

    /**
     * Получить список доступных голосов
     */
    getVoices() {
        return this.voices;
    }

    /**
     * Получить английские голоса
     */
    getEnglishVoices() {
        return this.voices.filter(v => 
            v.lang.startsWith('en-') || v.lang === 'en'
        );
    }

    /**
     * Установить предпочитаемый голос
     */
    setVoice(voiceName) {
        const voice = this.voices.find(v => v.name === voiceName);
        if (voice) {
            this.preferredVoice = voice;
        }
    }
}

// Создаем единственный экземпляр
const ttsService = new TextToSpeechService();

export default ttsService;
